import { faCheck, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoutePlanFeedbackSourceEnum } from 'api/model';
import {
  getRoutePlanFeedbackPackageQueryKey,
  useRoutePlanFeedbackAnswer,
  useRoutePlanFeedbackNextHook,
  useRoutePlanFeedbackPackage,
} from 'api/route-plan-feedback/route-plan-feedback';
import Button from 'components/inputs/Button';
import Routes from 'constants/Routes';
import useRoutePlanFeedbackAccess from 'hooks/useRoutePlanFeedbackAccess';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import AnswerForm, { AnswerValue } from './AnswerForm';
import {
  buildFeedbackPageUrl,
  describeOutdated,
  formatClock,
  formatPlanningDate,
  nextUnanswered,
  parseRating,
  THANKS_BEFORE_NEXT_MS,
} from './feedback-round-helpers';
import PackageView from './PackageView';
import ResultsView from './ResultsView';

const Page = styled.div`
  padding: 20px;
  display: grid;
  gap: 16px;
  max-width: 1280px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
`;

const Tab = styled.button<{ selected: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ selected, theme }) =>
    selected
      ? theme.colors.foreground.primary
      : theme.colors.background.primary};
  color: ${({ selected, theme }) =>
    selected
      ? theme.colors.background.primary
      : theme.colors.foreground.secondary};
  border-radius: 999px;
  padding: 5px 14px;
  font: inherit;
  cursor: pointer;
`;

/** Det viktigaste på sidan: att ingenting här planeras. */
const PreviewOnlyBanner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border: 2px solid ${({ theme }) => theme.colors.background.selection};
  background: ${({ theme }) => theme.colors.background.highlight};
  border-radius: 10px;
  padding: 12px 16px;

  p {
    margin: 2px 0 0;
  }
`;

const BannerIcon = styled.span`
  color: ${({ theme }) => theme.colors.background.selection};
  font-size: 1.3rem;
  line-height: 1;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Side = styled.div`
  display: grid;
  gap: 16px;
  align-content: start;
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
  padding: 16px;
  display: grid;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 1rem;
  }
  p {
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const pop = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  60% {
    transform: scale(1.15);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
`;

const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Nyckeln byts per paket, så att varje nytt förslag glider in från höger. */
const SlideIn = styled(Columns)`
  animation: ${slideIn} 0.35s ease-out;
  ${noMotion}
`;

const ThanksPanel = styled(Panel)`
  justify-items: center;
  text-align: center;
  padding: 28px 16px;
`;

const ThanksCheck = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.9rem;
  color: ${({ theme }) => theme.colors.foreground.good};
  background: ${({ theme }) => theme.colors.background.highlight};
  border: 2px solid ${({ theme }) => theme.colors.foreground.good};
  animation: ${pop} 0.45s ease-out;
  ${noMotion}
`;

const TextButton = styled.button`
  justify-self: start;
  background: none;
  border: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
`;

type Stage = 'answering' | 'thanks' | 'done' | 'empty';

const RoutePlanFeedbackPage: React.FC = () => {
  const { canGiveFeedback, canSeeNames, loaded } = useRoutePlanFeedbackAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fetchNext = useRoutePlanFeedbackNextHook();

  const tab = searchParams.get('flik') === 'resultat' ? 'results' : 'evaluate';
  const date = searchParams.get('datum') ?? undefined;
  const paketParam = searchParams.get('paket');
  const packageIndex = paketParam !== null ? Number(paketParam) : undefined;
  // Provet paketet visas ur. Dagen kan ha flera (14:00, 17:00, "Kör nu") och samma paketnummer är
  // ett annat paket i nästa, så sidan låser sig vid provet så fort paketet laddats (se nedan).
  const provParam = Number(searchParams.get('prov'));
  const runId =
    Number.isInteger(provParam) && provParam > 0 ? provParam : undefined;
  const preselectedRating = parseRating(searchParams.get('betyg'));

  const [stage, setStage] = useState<Stage>('answering');
  const [findingNext, setFindingNext] = useState(false);

  // Tacket står en stund innan nästa förslag glider in. Bläddrar man själv under tiden avbryts det.
  const advanceTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(advanceTimer.current), []);

  const goTo = useCallback(
    (planningDate: string, index: number, sampleRunId: number) => {
      window.clearTimeout(advanceTimer.current);
      setStage('answering');
      setSearchParams(
        {
          datum: planningDate,
          paket: String(index),
          prov: String(sampleRunId),
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  /**
   * Nästa paket att visa. Med `date` stannar vi på den dagens prov — så öppnar en länk med bara
   * `?datum=` (t.ex. "Öppna i Ruttfeedback" i inställningarna) rätt dag och inte morgondagens.
   * Överhoppningar sköts av bläddringslistan, så servern får inga `skip`.
   */
  const showNext = useCallback(
    async (options?: { date?: string }) => {
      setFindingNext(true);
      try {
        const forDate = options?.date;
        const response = await fetchNext(
          forDate ? { date: forDate } : undefined
        );
        if (response.next) {
          goTo(
            response.next.planningDateString,
            response.next.packageIndex,
            response.next.routePlanRunId
          );
        } else {
          setStage('empty');
        }
      } catch {
        setStage('empty');
      } finally {
        setFindingNext(false);
      }
    },
    [fetchNext, goTo]
  );

  // Slutpanelerna ("allt besvarat", "klar för idag") hör till adressen de visades på. Byts den —
  // bakåtknappen, en länk från toasten eller resultaten — visas paketet i stället.
  useEffect(() => {
    if (packageIndex !== undefined) {
      setStage((s) => (s === 'empty' || s === 'done' ? 'answering' : s));
    }
  }, [date, packageIndex, runId]);

  const switchTab = (params: Record<string, string>) => {
    // Annars hoppar tackets timer tillbaka till nästa förslag efter flikbytet.
    window.clearTimeout(advanceTimer.current);
    setStage('answering');
    setSearchParams(params);
  };

  // Utan paket i adressen: börja på nästa obesvarade.
  useEffect(() => {
    if (
      tab === 'evaluate' &&
      canGiveFeedback &&
      (!date || packageIndex === undefined) &&
      stage !== 'empty'
    ) {
      showNext({ date });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, canGiveFeedback, date, packageIndex, stage]);

  const packageParams =
    date && packageIndex !== undefined
      ? { date, packageIndex, ...(runId !== undefined ? { runId } : {}) }
      : undefined;
  const packageQuery = useRoutePlanFeedbackPackage(packageParams, {
    query: {
      enabled: !!packageParams && canGiveFeedback && tab === 'evaluate',
      // Låsningen nedan byter frågenyckel och fyller cachen för den nya — utan en kort färskhet
      // hade paketet hämtats en gång till direkt.
      staleTime: 30_000,
    },
  });

  // Utan `prov` i adressen (en länk med bara datum och paket) visas dagens senaste prov. Lås
  // adressen vid det så fort paketet hämtats: kommer ett nytt prov medan användaren svarar ska sidan
  // inte byta till ett annat paket med samma nummer. Bara efter en färsk hämtning — cachad data
  // under samma nyckel kan vara ett äldre prov. Cachen fylls först, så att bytet inte hämtar om.
  const loadedPackage = packageQuery.data;
  const freshlyLoaded =
    packageQuery.isFetchedAfterMount && !packageQuery.isFetching;
  useEffect(() => {
    if (
      !loadedPackage ||
      !freshlyLoaded ||
      runId !== undefined ||
      !date ||
      loadedPackage.planningDateString !== date ||
      loadedPackage.package.packageIndex !== packageIndex
    ) {
      return;
    }
    const pinned = { date, packageIndex, runId: loadedPackage.routePlanRunId };
    queryClient.setQueryData(
      getRoutePlanFeedbackPackageQueryKey(pinned),
      loadedPackage
    );
    const next = new URLSearchParams(searchParams);
    next.set('prov', String(loadedPackage.routePlanRunId));
    setSearchParams(next, { replace: true });
  }, [
    loadedPackage,
    freshlyLoaded,
    runId,
    date,
    packageIndex,
    queryClient,
    searchParams,
    setSearchParams,
  ]);

  const answerMutation = useRoutePlanFeedbackAnswer({
    mutation: {
      onSuccess: () => {
        setStage('thanks');
        // Alla paket i cachen: deras bläddringslista visar vad som är besvarat.
        queryClient.invalidateQueries({
          queryKey: getRoutePlanFeedbackPackageQueryKey(),
        });
      },
    },
  });

  if (!loaded) return null;
  if (!canGiveFeedback) {
    return <Navigate replace to={Routes.transportLogistics.followUp} />;
  }

  const { data } = packageQuery;
  const navigation = data?.navigation ?? [];
  const navPosition = data
    ? navigation.findIndex((n) => n.packageIndex === data.package.packageIndex)
    : -1;
  // Räknar med paketet man just svarat på, även innan listan hämtats om.
  const answeredAfterThis = navigation.filter(
    (n, i) => n.answered || i === navPosition
  ).length;

  const goToPackage = (index: number) => {
    if (data) goTo(data.planningDateString, index, data.routePlanRunId);
  };

  const submit = (value: AnswerValue) => {
    if (!date || packageIndex === undefined || !data) return;
    const next = nextUnanswered(navigation, packageIndex);
    const { planningDateString, routePlanRunId } = data;
    answerMutation.mutate(
      {
        data: {
          planningDateString: date,
          packageIndex,
          // Körningen paketet visades ur — svaret sparas på det provet, även om ett senare kommit.
          routePlanRunId: packageQuery.data?.routePlanRunId,
          rating: value.rating,
          changeTags: value.changeTags,
          comment: value.comment.trim() || undefined,
          source: RoutePlanFeedbackSourceEnum.Page,
        },
      },
      {
        onSuccess: () => {
          window.clearTimeout(advanceTimer.current);
          advanceTimer.current = window.setTimeout(() => {
            if (next !== undefined) {
              goTo(planningDateString, next, routePlanRunId);
            } else {
              setStage('empty');
            }
          }, THANKS_BEFORE_NEXT_MS);
        },
      }
    );
  };

  // Formuläret (och därmed "Hoppa över") visas bara för paket i listan.
  const skip = () => {
    if (!data) return;
    const next = nextUnanswered(navigation, data.package.packageIndex);
    if (next !== undefined) {
      goToPackage(next);
    } else {
      // Allt annat i provet är besvarat; det här lämnas obesvarat.
      setStage('done');
    }
  };

  return (
    <Page>
      <Header>
        <div>
          <Title>Ruttfeedback</Title>
          <Muted>
            Tyck till om ruttplaneringens förslag. Det tar under en minut per
            förslag.
          </Muted>
        </div>
        <Tabs role="tablist">
          <Tab
            role="tab"
            aria-selected={tab === 'evaluate'}
            selected={tab === 'evaluate'}
            onClick={() => switchTab({})}
          >
            Utvärdera
          </Tab>
          <Tab
            role="tab"
            aria-selected={tab === 'results'}
            selected={tab === 'results'}
            onClick={() => switchTab({ flik: 'resultat' })}
          >
            Resultat
          </Tab>
        </Tabs>
      </Header>

      {tab === 'results' ? (
        <ResultsView namesVisible={canSeeNames} />
      ) : (
        <>
          <PreviewOnlyBanner role="note">
            <BannerIcon>
              <FontAwesomeIcon icon={faCircleInfo} />
            </BannerIcon>
            <div>
              <b>Endast feedback – ingenting planeras härifrån.</b>
              <p>
                {data
                  ? `Förslaget togs fram automatiskt ${formatClock(data.generatedUtc)} för ${formatPlanningDate(data.planningDateString)}. `
                  : 'Förslagen tas fram automatiskt kl. 10:00, 14:00 och 17:00 för nästa dag. '}
                Det verkställs aldrig: inga uppdrag tilldelas, låses eller
                ändras, och dina svar påverkar inte hur dagen planeras. Planera
                som vanligt.
              </p>
            </div>
          </PreviewOnlyBanner>

          {stage === 'done' ? (
            <Panel>
              <h3>Tack för hjälpen!</h3>
              <p>
                Dina svar gör ruttplaneringen bättre. Du kan alltid komma
                tillbaka hit.
              </p>
              <Actions>
                <Button onClick={() => showNext()} loading={findingNext}>
                  Utvärdera ett förslag till
                </Button>
                <Link to={Routes.transportLogistics.followUp}>
                  Tillbaka till uppföljningen
                </Link>
              </Actions>
            </Panel>
          ) : stage === 'empty' ? (
            <Panel>
              <h3>Du har gått igenom allt för den här gången</h3>
              <p>Stort tack – och vi ses snart igen 🙂</p>
              <Muted>
                Förslag där något uppdrag redan har planerats sedan provet
                visas inte.
              </Muted>
              <Muted>Nästa prov tas fram kl. 14:00 eller 17:00.</Muted>
              <Actions>
                <Link
                  to={`${buildFeedbackPageUrl()}?flik=resultat`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchParams({ flik: 'resultat' });
                  }}
                >
                  Se vad andra svarat
                </Link>
              </Actions>
            </Panel>
          ) : packageQuery.isError ? (
            <Panel>
              <h3>Förslaget finns inte längre</h3>
              <p>Det kan vara för gammalt. Prova nästa förslag i stället.</p>
              <Actions>
                <Button onClick={() => showNext()} loading={findingNext}>
                  Visa nästa förslag
                </Button>
              </Actions>
            </Panel>
          ) : !data ? (
            <Muted>Hämtar förslaget …</Muted>
          ) : (
            <SlideIn
              key={`${data.routePlanRunId}-${data.package.packageIndex}`}
            >
              <PackageView
                data={data}
                navigation={
                  navPosition >= 0
                    ? {
                        position: navPosition + 1,
                        total: navigation.length,
                        answered: navigation[navPosition].answered,
                        onPrevious:
                          navPosition > 0
                            ? () =>
                                goToPackage(
                                  navigation[navPosition - 1].packageIndex
                                )
                            : undefined,
                        onNext:
                          navPosition < navigation.length - 1
                            ? () =>
                                goToPackage(
                                  navigation[navPosition + 1].packageIndex
                                )
                            : undefined,
                      }
                    : undefined
                }
              />
              <Side>
                {stage === 'thanks' ? (
                  <ThanksPanel role="status">
                    <ThanksCheck>
                      <FontAwesomeIcon icon={faCheck} />
                    </ThanksCheck>
                    <h3>Tack för feedbacken!</h3>
                    {navigation.length > 0 && (
                      <Muted>
                        {answeredAfterThis} av {navigation.length} besvarade
                      </Muted>
                    )}
                  </ThanksPanel>
                ) : navPosition < 0 ? (
                  // En gammal länk till ett paket utanför utvärderingen: inget formulär.
                  <Panel role="note">
                    <h3>Det här förslaget ingår inte i utvärderingen</h3>
                    <Actions>
                      <Button
                        onClick={() =>
                          showNext({ date: data.planningDateString })
                        }
                        loading={findingNext}
                      >
                        Visa nästa förslag
                      </Button>
                    </Actions>
                  </Panel>
                ) : (
                  <>
                    {data.isOutdated && (
                      <Panel role="note">
                        <h3>Förslaget är överspelat</h3>
                        <p>
                          {describeOutdated(
                            data.generatedUtc,
                            data.outdatedSinceUtc
                          )}
                        </p>
                        {/* Från ett äldre prov har panelen nedan redan samma knapp. */}
                        {data.isLatestSample && (
                          <Actions>
                            <Button
                              onClick={() =>
                                showNext({ date: data.planningDateString })
                              }
                              loading={findingNext}
                            >
                              Visa nästa förslag
                            </Button>
                          </Actions>
                        )}
                      </Panel>
                    )}
                    {!data.isLatestSample && (
                      <Panel role="note">
                        <h3>Ett senare prov finns</h3>
                        <p>
                          Det här förslaget togs fram{' '}
                          {formatClock(data.generatedUtc)}, och ett senare prov
                          för samma dag har kommit sedan dess. Du kan ändå svara
                          – svaret sparas på det här förslaget.
                        </p>
                        <Actions>
                          <Button
                            onClick={() =>
                              showNext({ date: data.planningDateString })
                            }
                            loading={findingNext}
                          >
                            Visa det senaste provet
                          </Button>
                        </Actions>
                      </Panel>
                    )}
                    <AnswerForm
                      key={`${data.routePlanRunId}-${data.package.packageIndex}`}
                      initial={data.myAnswer}
                      preselectedRating={preselectedRating}
                      sending={answerMutation.isLoading}
                      error={
                        answerMutation.isError
                          ? 'Svaret kunde inte sparas. Försök igen.'
                          : undefined
                      }
                      onSubmit={submit}
                      onSkip={skip}
                    />
                    <TextButton type="button" onClick={() => setStage('done')}>
                      Jag är klar för idag
                    </TextButton>
                  </>
                )}
                {navPosition >= 0 && (
                  <Panel>
                    <h3>Varför fick du just det här förslaget?</h3>
                    <p>
                      {data.selectionReason ??
                        'Du bad om fler förslag. Det här är ett av de andra paketen i samma körning.'}
                    </p>
                    <Muted>
                      Ett av {navigation.length} förslag i ruttprovet för{' '}
                      {formatPlanningDate(data.planningDateString)}.
                    </Muted>
                  </Panel>
                )}
              </Side>
            </SlideIn>
          )}
        </>
      )}
    </Page>
  );
};

export default RoutePlanFeedbackPage;
